import React, { memo, useCallback, type RefObject, type KeyboardEvent, useMemo } from 'react';
import { CONTEXTUAL_BULK_EDIT_ISSUE_LIMIT } from '@atlassian/jira-bulk-operations-core/src/common/constants';
import { useIntl } from '@atlassian/jira-intl';
import {
	type MenuNode,
	CONTEXT_MENU_APPEARANCE,
	MEATBALL_MENU_APPEARANCE,
} from '@atlassian/jira-software-context-menu';
import {
	AsyncContextMenu,
	AsyncMeatballMenu,
	AsyncMenuRenderer,
} from '@atlassian/jira-software-context-menu/src/async';
import { useShowCompactMode } from '@atlassian/jira-software-view-settings';
import { useBacklogSelector } from '../../../../state';
import { getWhiteboardEligibility } from '../../../../state/selectors/whiteboards-entrypoint-experiment';
import { useIsCMPBacklog } from '../../../../state/state-hooks/capabilities';
import { WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS } from '../../../whiteboards-entrypoint-experiment/constants';
import BlockDrag from './block-drag';
import { ANALYTICS_MAPPING_TABLE, ESCAPE_WRAPPER_TESTID } from './constants';
import { useAssignee } from './menu-items/assignee';
import { useCopyIssueKey } from './menu-items/copy-issue-key';
import { useCopyIssueLink } from './menu-items/copy-issue-link';
import { useDeleteIssue } from './menu-items/delete-issue';
import { useEstimate } from './menu-items/estimate';
import { useFlag } from './menu-items/flag';
import { useGoToBulkEditor } from './menu-items/go-to-bulk-editor';
import { useHeader } from './menu-items/header';
import { useMoveTo } from './menu-items/move-to';
import { useParent } from './menu-items/parent';
import { usePlanOnWhiteboard } from './menu-items/plan-on-whiteboard';
import { usePriority } from './menu-items/priority';
import { createSeparator } from './menu-items/separator';
import { useSplitIssue } from './menu-items/split-issue';
import { useVersion } from './menu-items/version';
import { useViewInIssueSearch } from './menu-items/view-in-issue-search';
import messages from './messages';
import type {
	UseBulkOperationProps,
	UseContextualMenuItemsProps,
	MenuProps,
	MenuRendererWithMenuItemsProps,
} from './types';
import useCardsIncludeSubtasksWithoutParents from './utils/use-cards-include-subtasks-without-parents';

export const useFieldBulkOperations = ({
	selectedCardIds,
	versionMenuQueryRef,
}: UseBulkOperationProps): MenuNode[] => {
	const priority = usePriority({ selectedCardIds });
	const assignee = useAssignee({ selectedCardIds });
	const estimate = useEstimate({ selectedCardIds });
	const parent = useParent(selectedCardIds);
	const version = useVersion({ selectedCardIds, versionMenuQueryRef });

	const isCompanyManaged = useIsCMPBacklog();

	const isWithinIssueLimit = selectedCardIds.length <= CONTEXTUAL_BULK_EDIT_ISSUE_LIMIT;
	return isWithinIssueLimit
		? [...assignee, ...priority, ...version, ...parent, ...(!isCompanyManaged ? estimate : [])]
		: [];
};

export const useContextMenuItems = ({
	issueId,
	issueKey,
	selectedCardIds,
	versionMenuQueryRef,
}: UseContextualMenuItemsProps) => {
	const cardIds = useMemo(
		() => (selectedCardIds.includes(issueId) ? selectedCardIds : [issueId]),
		[issueId, selectedCardIds],
	);

	const isMultiSelect = cardIds?.length > 1;
	const subtasksWithoutParentsAreSelected = useCardsIncludeSubtasksWithoutParents(cardIds);

	const header = useHeader({ selectedCardIds: cardIds });
	const moveToActions = useMoveTo({
		issueId,
		cardIds,
		hide: subtasksWithoutParentsAreSelected,
	});

	const flag = useFlag({ cardIds });
	const splitIssue = useSplitIssue({ issueId, isMultiSelect });
	const deleteIssue = useDeleteIssue({ issueId, selectedCardIds: cardIds });
	const copyIssueLink = useCopyIssueLink({
		selectedCardIds: cardIds,
		issueKey,
	});
	const copyIssueKey = useCopyIssueKey({
		selectedCardIds: cardIds,
		issueKey,
	});
	const viewInIssueSearch = useViewInIssueSearch({
		selectedCardIds: cardIds,
	});

	const gotoBulkEditor = useGoToBulkEditor({ selectedCardIds: cardIds });

	const fieldBulkOperations = useFieldBulkOperations({
		selectedCardIds: cardIds,
		versionMenuQueryRef,
	});

	// Check if the user is eligible for whiteboard entrypoint experiment and render the plan on whiteboard CTA below the moveTo button before the separator
	const { cohort, flow } = useBacklogSelector(getWhiteboardEligibility);
	const planOnWhiteboard = usePlanOnWhiteboard({
		issueKey,
		selectedCardIds,
		flow,
	});

	if (flow && cohort === WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS.CONTEXT_MENU) {
		moveToActions.push(...planOnWhiteboard);
	}

	const moveTo = moveToActions.length ? [...moveToActions, createSeparator('move-to-actions')] : [];
	const bulkActions: MenuNode[] = [...flag, ...fieldBulkOperations, ...splitIssue];
	const otherActions: MenuNode[] = [...copyIssueLink, ...copyIssueKey, ...viewInIssueSearch];

	const deleteAndBulkEditorActions =
		gotoBulkEditor.length || deleteIssue.length
			? [createSeparator('delete-and-bulk-editor-actions'), ...gotoBulkEditor, ...deleteIssue]
			: [];

	return [
		...header,
		...moveTo,
		...otherActions,
		...(bulkActions.length ? [createSeparator('bulk-actions'), ...bulkActions] : []),
		...deleteAndBulkEditorActions,
	];
};

const MenuRendererWithMenuItems = memo(
	({
		issueId,
		issueKey,
		selectedCardIds,
		scope,
		triggerRef,
		versionMenuQueryRef,
	}: MenuRendererWithMenuItemsProps) => {
		const menuItems = useContextMenuItems({
			issueId,
			issueKey,
			selectedCardIds,
			versionMenuQueryRef,
		});

		return (
			// Prevents drag events being propogated to the parent card
			<BlockDrag>
				<AsyncMenuRenderer scope={scope} menuItems={menuItems} triggerRef={triggerRef} />
			</BlockDrag>
		);
	},
);

export const ContextMenu = memo((props: MenuProps) => {
	const { issueId, issueKey, selectedCardIds, appearance, versionMenuQueryRef } = props;
	const { formatMessage } = useIntl();
	const showCompactMode = useShowCompactMode();
	const triggerRef = 'triggerRef' in props ? props.triggerRef : null;

	const renderMenu = useCallback(
		(scope: string, menuTriggerRef?: RefObject<HTMLButtonElement>) => {
			if (triggerRef && menuTriggerRef?.current) {
				triggerRef.current = menuTriggerRef.current;
			}

			return (
				<MenuRendererWithMenuItems
					issueId={issueId}
					issueKey={issueKey}
					selectedCardIds={selectedCardIds}
					versionMenuQueryRef={versionMenuQueryRef}
					scope={scope}
					triggerRef={menuTriggerRef}
				/>
			);
		},
		[triggerRef, issueId, issueKey, selectedCardIds, versionMenuQueryRef],
	);

	const handleEscape = (e: KeyboardEvent) => {
		// this is to prevent the escape key from closing the issue view
		if (e.key === 'Escape') {
			e.stopPropagation();
			e.nativeEvent.stopImmediatePropagation();
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}
		}
	};

	if (appearance === CONTEXT_MENU_APPEARANCE) {
		const { children } = props;

		return (
			// disabling the eslint rule since it asks us to turn this into a button which does not fit our use case
			<div onKeyDown={handleEscape} data-testid={ESCAPE_WRAPPER_TESTID} role="presentation">
				<AsyncContextMenu
					scope={`${issueId}`}
					renderMenu={renderMenu}
					selectedCardIds={selectedCardIds}
					cardId={issueId}
					analyticsMapping={ANALYTICS_MAPPING_TABLE}
					jsErrorBoundaryProps={{
						id: 'backlogCardListCardContextMenu',
						packageName: 'software-backlog',
						teamName: 'a4t-tanuki',
					}}
				>
					{children}
				</AsyncContextMenu>
			</div>
		);
	}

	if (appearance === MEATBALL_MENU_APPEARANCE) {
		const { summary, onOpenChange } = props;

		const ariaLabelMessage = formatMessage(
			selectedCardIds.length > 1 ? messages.menuTriggerMoreCards : messages.menuTriggerOneCard,
			{
				summary,
				issueKey,
			},
		);

		return (
			<AsyncMeatballMenu
				scope={`${issueId}`}
				renderMenu={renderMenu}
				onOpenChange={onOpenChange}
				selectedCardIds={selectedCardIds}
				cardId={issueId}
				analyticsMapping={ANALYTICS_MAPPING_TABLE}
				ariaLabel={ariaLabelMessage}
				isCompact={showCompactMode}
				jsErrorBoundaryProps={{
					id: 'backlogCardListCardMeatballMenu',
					packageName: 'software-backlog',
					teamName: 'a4t-tanuki',
				}}
			/>
		);
	}

	return null;
});
